import React from 'react';
import { View, Text, TouchableOpacity, Modal, TouchableWithoutFeedback, Platform, Dimensions, ScrollView, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgXml } from 'react-native-svg';
import { useTheme } from '../../../../../theme';
import type { CalendarEvent, CalendarStyleConfig } from '../types';
import { emailToDisplayName } from '../../../../../screens/ActionPlan/utils/formOptions';

const EyeIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.8 17.5 12 17.5C8.2 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="white"/>
</svg>`;

interface EventDetailsPopoverProps {
  event: CalendarEvent | null;
  visible: boolean;
  onClose: () => void;
  onViewDetails?: (event: CalendarEvent) => void;
  styleConfig?: CalendarStyleConfig;
  eventData?: any; // Full API action plan data
}

/**
 * EventDetailsPopover Component
 * Displays event details in a modal popover following Figma design
 * - Web/iPad: Centered modal popover
 * - iOS Mobile: Bottom sheet modal (matching Figma design)
 */
export const EventDetailsPopover: React.FC<EventDetailsPopoverProps> = ({
  event,
  visible,
  onClose,
  onViewDetails,
  eventData,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  if (!event) {
    return null;
  }

  // Device detection - Only iOS mobile (not iPad) should show bottom sheet
  const { width } = Dimensions.get('window');
  const isTablet = width >= 768;
  const isIOSMobile = Platform.OS === 'ios' && !isTablet;

  // Safe area insets for padding (ensure sticky footer button is visible)
  const insets = useSafeAreaInsets();

  // Format date to readable format WITHOUT timezone conversion
  const formatDate = (date: Date | string): string => {
    try {
      let year: number, month: number, day: number;
      
      if (typeof date === 'string') {
        // Parse ISO string without timezone conversion
        const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          year = parseInt(isoMatch[1], 10);
          month = parseInt(isoMatch[2], 10) - 1; // 0-indexed
          day = parseInt(isoMatch[3], 10);
        } else {
          return date;
        }
      } else {
        // Date object - use its components directly
        year = date.getFullYear();
        month = date.getMonth();
        day = date.getDate();
      }
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return `${monthNames[month]} ${day}, ${year}`;
    } catch (e) {
      return typeof date === 'string' ? date : '--';
    }
  };

  // Get status color based on ASSIGNMENT_STATUS first, then STATUS field
  const getStatusColor = (): string => {
    const assignmentStatus = eventData?.ASSIGNMENT_STATUS?.toLowerCase();
    const status = eventData?.STATUS?.toLowerCase();
    
    // Check ASSIGNMENT_STATUS first
    if (assignmentStatus === 'declined') {
      return '#D40020'; // Red for declined
    }
    
    // Then check STATUS
    switch (status) {
      case 'completed':
        return '#008553'; // Green
      case 'in_progress':
        return '#316BB8'; // Blue
      case 'overdue':
        return '#CC6E16'; // Orange
      default:
        return '#316bb8';
    }
  };

  const assignmentStatus = eventData?.ASSIGNMENT_STATUS?.toLowerCase();
  const status = assignmentStatus === 'declined' ? 'declined' : (eventData?.STATUS || 'pending');
  const statusLabel = assignmentStatus === 'declined'
    ? 'Declined'
    : status === 'in_progress'
    ? 'In Progress'
    : status === 'completed'
      ? 'Completed'
      : status === 'overdue'
      ? 'Overdue'
      : status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');

  // Render content section (shared between popover and bottom modal)
  const renderContent = () => (
    <View
      style={{
        backgroundColor: isDark ? colors.background.surface : '#ffffff',
        borderWidth: 1,
        borderColor: isDark ? colors.border.default : '#ebebeb',
        borderRadius: 8,
        padding: 12,
        gap: 16,
      }}
    >
      {/* Status Tag */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
        <Text style={{ fontSize: 14, fontWeight: '700', color: isDark ? colors.text.primary : '#2d2d2d', flex: 1, textTransform: 'capitalize' }} numberOfLines={1}>
          {eventData?.TITLE || event.title}
        </Text>
        <View
          style={{
            backgroundColor: getStatusColor(),
            borderRadius: 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}
        >
          <Text style={{ fontSize: 10, fontWeight: '600', color: '#ffffff', lineHeight: 14 }}>
            {statusLabel}
          </Text>
        </View>
      </View>

      {/* Details Section */}
      <View style={{ flexDirection: 'column', gap: 16 }}>
        {/* Assigned User and Source */}
        <View style={{ flexDirection: 'column', gap: 16 }}>
          <View style={{ flex: 1, gap: 4 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Currently assigned</Text>
            <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
              {eventData?.ASSIGNEE_NAME || emailToDisplayName(eventData?.ASSIGNEE_EMAIL || '') || eventData?.ASSIGNEE_GPID || 'N/A'}
            </Text>
          </View>

          <View style={{ flex: 1, gap: 4 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Source</Text>
            <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
              {eventData?.SOURCE ? eventData.SOURCE.replace(/_/g, ' ') : 'N/A'}
            </Text>
          </View>
        </View>

        {/* Action Plan Description */}
        <View style={{ gap: 4 }}>
          <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Action Plan</Text>
          <Text
            style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d', lineHeight: 20, textTransform: 'capitalize' }}
            numberOfLines={3}
          >
            {eventData?.ACTION_PLAN_DESCRIPTION ? eventData.ACTION_PLAN_DESCRIPTION : 'N/A'}
          </Text>
        </View>

        {/* Type and Target Date */}
        <View style={{ flexDirection: 'row', gap: 16 }}>
          <View style={{ flex: 1, gap: 4 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Type</Text>
            <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
              {eventData?.TYPE || 'N/A'}
            </Text>
          </View>
          <View style={{ flex: 1, gap: 4 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Target date</Text>
            <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d' }}>
              {eventData?.TARGET_DATE ? formatDate(eventData.TARGET_DATE) : 'N/A'}
            </Text>
          </View>
        </View>
      </View>

    </View>
  );

  // iOS Mobile: Bottom Sheet Modal
  if (isIOSMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
        <View style={styles.bottomModalContainer}>
          <TouchableOpacity 
            style={styles.bottomModalOverlay}
            activeOpacity={1}
            onPress={onClose}
          />
          <View style={[styles.bottomModalContent, isDark && { backgroundColor: colors.background.surface }]}>
            {/* Resize Indicator */}
            <View style={[styles.resizeIndicator, isDark && { backgroundColor: colors.border.default }]} />

            {/* Header */}
            <View style={[styles.bottomModalHeader, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
              <Text style={[styles.modalHeaderTitle, isDark && { color: colors.text.primary }]} numberOfLines={1} ellipsizeMode="tail">Action Plan</Text>
              <TouchableOpacity
                onPress={onClose}
                style={styles.bottomModalCloseButton}
                accessibilityLabel="Close"
                activeOpacity={0.7}
              >
                <Text style={{ fontSize: 28, fontWeight: 'bold', color: isDark ? colors.text.primary : '#333' }}>×</Text>
              </TouchableOpacity>
            </View>
            
            {/* Scrollable Content */}
            <ScrollView
              style={styles.bottomModalScroll}
              contentContainerStyle={styles.bottomModalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              {renderContent()}
            </ScrollView>

            {/* Sticky Footer with View Details Button */}
            <View style={[
              styles.stickyFooter,
              isDark && { backgroundColor: colors.background.surface, shadowColor: 'transparent' },
              { paddingBottom: (0 + (insets?.bottom || 0)) },
            ]}>
              <TouchableOpacity
                style={[styles.viewDetailsButton, isDark && { backgroundColor: colors.brand.primary }]}
                onPress={() => {
                  onViewDetails?.(event);
                  onClose();
                }}
                activeOpacity={0.8}
              >
                <SvgXml xml={EyeIcon} width="20" height="20" />
                <Text style={styles.viewDetailsButtonText}>View details</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Web/iPad: Centered Popover Modal (existing design)
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <TouchableWithoutFeedback>
            <View
              style={{
                backgroundColor: isDark ? colors.background.surface : '#ffffff',
                borderRadius: 8,
                padding: 16,
                width: '85%',
                maxWidth: 400,
                maxHeight: '90%',
                gap: 8,
              }}
            >
              {/* Header */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: isDark ? colors.text.primary : '#2d2d2d', flex: 1, textTransform: 'capitalize' }} numberOfLines={1}>
                  Action Plan
                </Text>
                <TouchableOpacity onPress={onClose} style={{ padding: 4 }}>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: isDark ? colors.text.primary : '#3a3a3a' }}>×</Text>
                </TouchableOpacity>
              </View>

              {/* Scrollable Content Wrapper */}
              <ScrollView
                style={{ flexShrink: 1 }}
                contentContainerStyle={{ flexGrow: 1 }}
                showsVerticalScrollIndicator={true}
              >
                {/* Content */}
                <View
                  style={{
                    backgroundColor: isDark ? colors.background.elevated : '#ffffff',
                    borderWidth: 1,
                    borderColor: isDark ? colors.border.default : '#ebebeb',
                    borderRadius: 8,
                    padding: 12,
                    gap: 16,
                  }}
                >
                {/* Status Tag */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: isDark ? colors.text.primary : '#2d2d2d', flex: 1, textTransform: 'capitalize' }} numberOfLines={1}>
                    {eventData?.TITLE || event.title}
                  </Text>
                  <View
                    style={{
                      backgroundColor: getStatusColor(),
                      borderRadius: 4,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                    }}
                  >
                    <Text style={{ fontSize: 10, fontWeight: '600', color: '#ffffff', lineHeight: 14 }}>
                      {statusLabel}
                    </Text>
                  </View>
                </View>

                {/* Details Section */}
                <View style={{ flexDirection: 'column', gap: 16 }}>
                  {/* Assigned User and Source */}
                  <View style={{ flexDirection: 'column', gap: 16 }}>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Currently assigned</Text>
                      <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
                        {eventData?.ASSIGNEE_NAME || emailToDisplayName(eventData?.ASSIGNEE_EMAIL || '') || eventData?.ASSIGNEE_GPID || 'N/A'}
                      </Text>
                    </View>

                    <View style={{ flex: 1, gap: 4 }}>
                      <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Source</Text>
                      <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
                        {eventData?.SOURCE ? eventData.SOURCE.replace(/_/g, ' ') : 'N/A'}
                      </Text>
                    </View>
                  </View>

                  {/* Action Plan Description */}
                  <View style={{ gap: 4 }}>
                    <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Action Plan</Text>
                    <Text
                      style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d', lineHeight: 20, textTransform: 'capitalize' }}
                      numberOfLines={3}
                    >
                      {eventData?.ACTION_PLAN_DESCRIPTION ? eventData.ACTION_PLAN_DESCRIPTION : 'N/A'}
                    </Text>
                  </View>

                  {/* Type and Target Date */}
                  <View style={{ flexDirection: 'row', gap: 16 }}>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Type</Text>
                      <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#000000' }}>
                        {eventData?.TYPE || 'N/A'}
                      </Text>
                    </View>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b' }}>Target date</Text>
                      <Text style={{ fontSize: 14, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d' }}>
                        {eventData?.TARGET_DATE ? formatDate(eventData.TARGET_DATE) : 'N/A'}
                      </Text>
                    </View>
                  </View>
                </View>

                  {/* Action Button Inside Content Card */}
                  <TouchableOpacity
                    style={{
                      backgroundColor: isDark ? colors.brand.primary : '#005cbc',
                      borderRadius: 24,
                      height: 40,
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: 8,
                      flexDirection: 'row',
                      gap: 8,
                      paddingHorizontal: 12,
                    }}
                    onPress={() => {
                      onViewDetails?.(event);
                      onClose();
                    }}
                  >
                    <SvgXml xml={EyeIcon} width="20" height="20" />
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#ffffff' }}>View details</Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

// Styles for bottom modal (iOS mobile)
const styles = StyleSheet.create({
  bottomModalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  bottomModalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  bottomModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
    paddingTop: 6,
    flexShrink: 0,
  },
  resizeIndicator: {
    width: 36,
    height: 5,
    backgroundColor: '#DADADA',
    borderRadius: 2.5,
    alignSelf: 'center',
    marginBottom: 8,
  },
  bottomModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#ffffff',
    minHeight: 54,
  },
  bottomModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1f1f1f',
    flex: 1,
    paddingRight: 12,
  },
  bottomModalCloseButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  modalHeaderTitle: {
    overflow: 'hidden',
    color: '#2D2D2D',
    fontFamily: 'Inter',
    fontSize: 28,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 40,
  },
  bottomModalScroll: {
    flexGrow: 1,
    flexShrink: 1,
  },
  bottomModalScrollContent: {
    padding: 16,
    paddingBottom: 16,
  },
  stickyFooter: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
    borderTopWidth: 0,
    shadowColor: '#005EBC',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 8,
  },
  viewDetailsButton: {
    backgroundColor: '#034895',
    borderRadius: 24,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  viewDetailsButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: 22,
  },
});

export default EventDetailsPopover;
